import React, { useState } from 'react';
import { getHeaders } from '../api';
import { canOpenViewOnly, isViewer, isReadOnlyStaff, hasUnmappedProfile, hasStaffRole } from '../lib/staffAccess';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

// The OTP consent boundary, as a modal. App owns the one instance: every path
// that opens an account (Customer Lookup, Dashboard, Tickets, Sales, a pasted
// #account/<id> link) asks App, and App asks this when the account is locked.

const OVERRIDE_MIN_REASON_LEN = 10; // mirrors OTP_OVERRIDE_MIN_REASON_LEN server-side

export default function OTPModal({ accountId, accountName, onVerified, onCancel, me = null }) {
  const [step, setStep]       = useState('send');
  const [code, setCode]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [reason, setReason]   = useState('');
  // The override is open to every mapped staff role (admin, sales, support;
  // 2026-11-02, it was admin-only before) and the server enforces that (403).
  // This flag only decides whether to OFFER it — a viewer shown a button that
  // always fails would learn to distrust the modal. `me` comes from App's own
  // whoami, so a login that is still resolving or has no role sees the ordinary
  // OTP path.
  const canOverride = hasStaffRole(me);

  // A VIEW-ONLY staffer has a different door, and only that one: no code to
  // send (sending one is a write, and the server refuses a viewer every
  // write), no reason to type. The account opens read-only for an hour and the
  // open goes on its timeline. Decided at render from App's whoami rather than
  // held in `step`, so a modal raised before whoami resolved (a pasted
  // #account/<id> link) changes shape when the answer arrives.
  const viewOnly = isViewer(me);
  // Read-only and NOT a viewer: a login with no staff role at all. Neither
  // door is open to it, so say that instead of offering a button that 403s.
  const noRole = !viewOnly && isReadOnlyStaff(me);
  const mode = noRole ? 'norole' : viewOnly ? 'viewonly' : step;
  // LICENSED STAFF may look without a code too — the customer's code is
  // consent to act, not to look. For them it is a second option under the
  // code, not the only door: they are usually here to do something.
  const offerViewOnly = !viewOnly && !noRole && canOpenViewOnly(me);

  const handleViewOnly = async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/otp/view-only`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ accountId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not open the account');
      onVerified(data.token, { readOnly: true });
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  // Break-glass: open the account with no code, because there is nobody on the
  // customer side to read one back — a 2am incident, an abandoned cart, a case
  // with no live contact. Costs a typed reason and lands a red line in the
  // account's Events timeline.
  const handleOverride = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < OVERRIDE_MIN_REASON_LEN) return;
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/otp/override`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ accountId, reason: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Override failed');
      onVerified(data.token);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleSend = async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/otp/send`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ accountId, accountName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code');
      setStep('verify');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/otp/verify`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ accountId, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      onVerified(data.token);
    } catch (e) {
      setError(e.message);
      setCode('');
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }} onClick={onCancel}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16, width: '90%', maxWidth: 400, padding: 32, boxShadow: '0 25px 60px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>{mode === 'override' ? '⚠' : mode === 'viewonly' ? '👁' : '🔐'}</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
            {mode === 'override' ? 'Open Without Customer Confirmation'
              : mode === 'viewonly' ? 'Open View-Only'
              : mode === 'norole' ? 'No Staff Role'
              : 'Account Access Required'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)', fontFamily: 'var(--mono)', lineHeight: 1.5 }}>
            {mode === 'send' && <><span>Verify your identity to access</span><br /><span style={{ color: 'var(--accent)' }}>{accountName}</span></>}
            {mode === 'verify' && <span>Enter the 6-digit code sent to your email</span>}
            {mode === 'override' && <><span>Skipping consent on</span><br /><span style={{ color: 'var(--accent)' }}>{accountName}</span></>}
            {mode === 'viewonly' && <><span>Read-only access to</span><br /><span style={{ color: 'var(--accent)' }}>{accountName}</span></>}
            {mode === 'norole' && <span>This login cannot open customer accounts</span>}
          </div>
        </div>

        {error && <div style={{ background: 'rgba(250,109,138,0.1)', border: '1px solid rgba(250,109,138,0.3)', color: 'var(--accent2)', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16, textAlign: 'center' }}>{error}</div>}

        {mode === 'norole' ? (
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, lineHeight: 1.5 }}>
            {hasUnmappedProfile(me)
              ? <>You have a Salesforce seat, but its profile{me?.profileName ? <> (<b>{me.profileName}</b>)</> : null} is not mapped to a CRM role, so you cannot open accounts or change anything yet. Ask an admin to map it.</>
              : <>Your login has no staff role, so it can neither send a customer an access code nor open an account view-only. Ask an admin to add you.</>}
          </div>
        ) : mode === 'viewonly' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, lineHeight: 1.5 }}>
              No customer code is needed. You can read everything on this account and change nothing. Your email goes on the account&apos;s timeline each time you open it, and the access expires in 1 hour.
            </div>
            <button autoFocus onClick={handleViewOnly} disabled={loading} style={{ width: '100%', padding: '12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'var(--font)', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Opening…' : 'Open View-Only'}
            </button>
          </div>
        ) : step === 'override' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: 'var(--text2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, lineHeight: 1.5 }}>
              The customer is not confirming this access. Your email and this reason go on the account&apos;s timeline, and the unlock expires in 1 hour.
            </div>
            <textarea
              autoFocus
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Why can't the customer confirm? e.g. Case 01234 — abandoned cart, no reply since Tuesday"
              rows={3}
              style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)', outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5 }}
            />
            <button onClick={handleOverride} disabled={loading || reason.trim().length < OVERRIDE_MIN_REASON_LEN} style={{ width: '100%', padding: '12px', background: reason.trim().length >= OVERRIDE_MIN_REASON_LEN ? '#f87171' : 'var(--bg3)', color: reason.trim().length >= OVERRIDE_MIN_REASON_LEN ? '#fff' : 'var(--text3)', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: reason.trim().length >= OVERRIDE_MIN_REASON_LEN && !loading ? 'pointer' : 'not-allowed', fontFamily: 'var(--font)', transition: 'all 0.15s' }}>
              {loading ? 'Recording…' : 'Override & Open Account'}
            </button>
            <button onClick={() => { setStep('send'); setReason(''); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--mono)', textAlign: 'center' }}>
              Back to sending a code
            </button>
          </div>
        ) : step === 'send' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button onClick={handleSend} disabled={loading} style={{ width: '100%', padding: '12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'var(--font)', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Sending…' : 'Send Access Code via Email'}
            </button>
            {offerViewOnly && (
              <button onClick={handleViewOnly} disabled={loading} title="Read-only for 1 hour, recorded on the account's timeline. To change anything you will still need the customer's code." style={{ width: '100%', padding: '10px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'var(--font)' }}>
                Just looking? Open view-only
              </button>
            )}
            {canOverride && (
              <button onClick={() => { setStep('override'); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--mono)', textAlign: 'center' }}>
                No one to confirm? Open without a code
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              autoFocus
              value={code}
              onChange={e => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              placeholder="000000"
              style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px', color: 'var(--text)', fontSize: 28, fontFamily: 'var(--mono)', fontWeight: 700, textAlign: 'center', outline: 'none', letterSpacing: '0.3em', width: '100%', boxSizing: 'border-box' }}
            />
            <button onClick={handleVerify} disabled={loading || code.length !== 6} style={{ width: '100%', padding: '12px', background: code.length === 6 ? 'var(--accent)' : 'var(--bg3)', color: code.length === 6 ? '#fff' : 'var(--text3)', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: code.length === 6 && !loading ? 'pointer' : 'not-allowed', fontFamily: 'var(--font)', transition: 'all 0.15s' }}>
              {loading ? 'Verifying…' : 'Verify & Open Account'}
            </button>
            <button onClick={() => { setStep('send'); setCode(''); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--mono)', textAlign: 'center' }}>
              Resend code
            </button>
          </div>
        )}

        <button onClick={onCancel} style={{ width: '100%', marginTop: 12, background: 'transparent', border: '1px solid var(--border)', borderRadius: 10, padding: '10px', color: 'var(--text3)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
